// ==========================================
// 1. REGEX PATTERNS
// ==========================================
export const REGEX = {
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  PHONE: /^[0-9]{7,15}$/,
  URL: /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/,
  PASSWORD: /^(?=.*[A-Z])(?=.*\d).{8,}$/, // Min 8 chars, 1 uppercase, 1 number

  // String & Binary Formats
  BINARY_STRING: /^[01]+$/,                // Strict 0s and 1s (e.g. "10101011")
  HEX_STRING: /^[0-9a-fA-F]+$/,            // Hexadecimal string (e.g. "4a6f")
  BASE64_STRING: /^([A-Za-z0-9+/]{4})*([A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{2}==)?$/,
  ALPHA_NUMERIC: /^[a-zA-Z0-9]+$/,         // Letters and numbers only
};

// ==========================================
// 2. ASYNC IMAGE VALIDATOR (Size, Type, Dimensions)
// ==========================================
/**
 * Validates an image file or existing path/URL string.
 * @param {File|string} file - File object or existing image path URL string
 * @param {Object} options - { allowedTypes, maxSizeMB, requiredWidth, requiredHeight, required }
 * @returns {Promise<string|null>} Returns error message string if invalid, or null if valid.
 */
export const validateImageFile = (file, options = {}) => {
  return new Promise((resolve) => {
    // 1. Check if empty
    if (!file) {
      return resolve(options.required ? `${options.label || "Image"} is required` : null);
    }

    // 2. If it's an existing uploaded image URL/path (string), consider it valid
    if (typeof file === "string" && file.trim() !== "") {
      return resolve(null);
    }

    // 3. If it's not a File object at this point, return error
    if (!(file instanceof File)) {
      return resolve("Invalid file format");
    }

    // 4. Check File Mime Type
    const allowedTypes = options.allowedTypes || ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      return resolve(
        `Only ${allowedTypes.map((t) => t.split("/")[1].toUpperCase()).join(", ")} images are allowed`
      );
    }

    // 5. Check File Size (in Megabytes)
    const maxSizeMB = options.maxSizeMB || 5; // Default 5MB
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return resolve(`Image size must be less than ${maxSizeMB}MB`);
    }

    // 6. Check Image Resolution / Dimensions (if width & height are specified)
    if (options.requiredWidth && options.requiredHeight) {
      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(img.src);
        if (img.width !== options.requiredWidth || img.height !== options.requiredHeight) {
          return resolve(
            `Image dimensions must be exactly ${options.requiredWidth} × ${options.requiredHeight} px`
          );
        }
        return resolve(null); // Valid
      };

      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        return resolve("Invalid or corrupted image file");
      };
    } else {
      return resolve(null); // Valid (no resolution check requested)
    }
  });
};

// ==========================================
// 3. UNIVERSAL FORM VALIDATION ENGINE
// ==========================================
/**
 * Universal Form Validator Engine
 * @param {Object} formData - Key-value pair of form data
 * @param {Object} schema - Validation rules
 * @returns {Object} errors - Key-value pair of error messages
 */
export const validateForm = async (formData = {}, schema = {}) => {
  const errors = {};

  for (const field of Object.keys(schema)) {
    const value = formData[field];
    const rules = schema[field];

    if (!rules) continue;

    // Special Handling for Image Type (Works for both File object and existing string URL)
    if (rules.type === "image") {
      const imageError = await validateImageFile(value, {
        ...rules.imageOptions,
        required: rules.required,
        label: rules.label || field,
      });
      if (imageError) {
        errors[field] = imageError;
      }
      continue; // Skip remaining standard string/number checks for image fields
    }

    // 1. Standard Required Check
    if (
      rules.required &&
      (value === undefined ||
        value === null ||
        (typeof value === "string" && !value.trim()) ||
        (Array.isArray(value) && value.length === 0))
    ) {
      errors[field] = rules.requiredMessage || `${rules.label || field} is required`;
      continue;
    }

    // Skip optional empty fields
    if (!rules.required && (value === undefined || value === null || value === "")) {
      continue;
    }

    // 2. Numeric Validations (Number, Min value, Max value)
    if (rules.type === "number") {
      const numValue = Number(value);

      if (isNaN(numValue)) {
        errors[field] = rules.invalidMessage || `${rules.label || "Field"} must be a valid number`;
        continue;
      }

      if (rules.min !== undefined && numValue < rules.min) {
        errors[field] = rules.minMessage || `${rules.label || "Field"} must be at least ${rules.min}`;
        continue;
      }

      if (rules.max !== undefined && numValue > rules.max) {
        errors[field] = rules.maxMessage || `${rules.label || "Field"} cannot exceed ${rules.max}`;
        continue;
      }
    }

    const strValue = typeof value === "string" ? value.trim() : value;

    // 3. Binary / String Formats
    if (rules.type === "binary" && !REGEX.BINARY_STRING.test(strValue)) {
      errors[field] = rules.invalidMessage || `${rules.label || "Field"} must contain 0s and 1s only`;
      continue;
    }

    if (rules.type === "hex" && !REGEX.HEX_STRING.test(strValue)) {
      errors[field] = rules.invalidMessage || `${rules.label || "Field"} must be a valid hex string`;
      continue;
    }

    if (rules.type === "base64" && !REGEX.BASE64_STRING.test(strValue)) {
      errors[field] = rules.invalidMessage || `${rules.label || "Field"} must be a valid base64 string`;
      continue;
    }

    // 4. Standard Formats (Email, Phone, URL, Password)
    if (rules.type === "email" && !REGEX.EMAIL.test(strValue)) {
      errors[field] = rules.invalidMessage || `Invalid ${rules.label || "email address"}`;
      continue;
    }

    if (rules.type === "phone" && !REGEX.PHONE.test(strValue)) {
      errors[field] = rules.invalidMessage || `Invalid ${rules.label || "phone number"}`;
      continue;
    }

    if (rules.type === "url" && !REGEX.URL.test(strValue)) {
      errors[field] = rules.invalidMessage || `Invalid ${rules.label || "URL"}`;
      continue;
    }

    if (rules.type === "password" && !REGEX.PASSWORD.test(strValue)) {
      errors[field] =
        rules.invalidMessage || "Min 8 chars, 1 uppercase letter & 1 number required";
      continue;
    }

    // 5. Min / Max String Length Checks
    if (rules.minLength && String(strValue).length < rules.minLength) {
      errors[field] = `${rules.label || "Field"} must be at least ${rules.minLength} characters`;
      continue;
    }

    if (rules.maxLength && String(strValue).length > rules.maxLength) {
      errors[field] = `${rules.label || "Field"} cannot exceed ${rules.maxLength} characters`;
      continue;
    }

    // 6. Cross-field Comparison (e.g. adminEmail !== companyEmail)
    if (rules.notEqualTo && formData[rules.notEqualTo]) {
      const targetVal = String(formData[rules.notEqualTo]).trim();
      if (String(strValue).toLowerCase() === targetVal.toLowerCase()) {
        errors[field] =
          rules.notEqualMessage ||
          `Must be different from ${schema[rules.notEqualTo]?.label || rules.notEqualTo}`;
        continue;
      }
    }

    // 7. Custom Rule Function
    if (typeof rules.custom === "function") {
      const customError = rules.custom(value, formData);
      if (customError) {
        errors[field] = customError;
      }
    }
  }

  return errors;
};